import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Snackbar,
  Stack,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableSortLabel,
  Link,
  TextField,
  Chip,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import SendIcon from '@mui/icons-material/Send'
import PaymentsIcon from '@mui/icons-material/Payments'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import DownloadIcon from '@mui/icons-material/Download'
import Env from '../../shared/config/envConfig'
import { addCsrfHeaders } from '../../shared/config/csrf'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'

interface ParticipantRow {
  id: string
  name?: string
  participationOptionName?: string
  company?: string
  email?: string
  invoiceNumber?: string
  invoiceId?: string
  sentAt?: string | Date | null
  selected?: boolean
  amountOpen?: number
  paid?: boolean
  amountGross?: number
}

// Event info shape as provided by backend; using flexible mapping to be robust
interface EventInfo {
  id?: string
  uuid?: string
  name?: string
  title?: string
  label?: string
  startDate?: string
  endDate?: string
  location?: string
  venue?: string
  place?: string
  city?: string
}

// Mail template type (analog zu Invoice.tsx)
interface MailTemplate {
  id?: string
  key?: string
  name?: string
  subject?: string
  body?: string
  text?: string
  content?: string
  type?: string
}

const useStyles = makeStyles(() => ({
  topBar: { display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' },
  tableHeadCell: { fontWeight: 600 },
}))

const EventInvoice: React.FC = () => {
  const { invoiceId: targetInvoiceId } = useParams<{ invoiceId?: string }>()
  const classes = useStyles()

  const [eventId, setEventId] = useState<string>('')
  const [events, setEvents] = useState<EventInfo[]>([])
  const [loadingEvents, setLoadingEvents] = useState<boolean>(false)
  const [rows, setRows] = useState<ParticipantRow[]>([])
  const [loading, setLoading] = useState<null | 'load' | 'generate' | 'send' | 'download' | 'pdf'>(null)
  const [snackOpen, setSnackOpen] = useState(false)
  const [snackMsg, setSnackMsg] = useState('')

  // Aktuell ausgewähltes Event ermitteln (für Template-Platzhalter)
  const selectedEvent = useMemo(() => {
    if (!eventId) return undefined
    return events.find(ev => String(ev.id || ev.uuid) === String(eventId))
  }, [events, eventId])

  // Sorting state
  const [sortKey, setSortKey] = useState<'name' | 'participationOptionName' | 'email' | 'invoiceNumber' | 'sentAt' | 'amountOpen'>('name')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

  // Per-row email dialog state
  const [sendDialogOpen, setSendDialogOpen] = useState(false)
  const [activeRow, setActiveRow] = useState<ParticipantRow | null>(null)
  const [emailText, setEmailText] = useState<string>('')
  const [sendingSingle, setSendingSingle] = useState<boolean>(false)
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false)
  const [paymentAmount, setPaymentAmount] = useState<string>('')
  const [paymentDate, setPaymentDate] = useState<string>(new Date().toISOString().split('T')[0])
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false)

  // Mailtemplates & Jahr (analog Invoice.tsx)
  const [mailTemplates, setMailTemplates] = useState<MailTemplate[] | null>(null)

  const disableActions = useMemo(() => !eventId || loading !== null, [eventId, loading])

  // Quill toolbar (leichtgewichtig)
  const quillModules = useMemo(() => ({
    toolbar: [
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      ['clean'],
    ],
  }), [])

  const quillFormats = useMemo(() => [
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet',
    'link',
  ], [])

  // Ersetzt Platzhalter im Template-HTML analog Invoice.tsx
  // Unterstützte Platzhalter:
  // {salutation} | {saluation} (Schreibfehler-Variante), {year}, {eventName}, {location}, {eventFrom}, {eventTo}
  const applyMailPlaceholders = useCallback((html: string, row: ParticipantRow): string => {
    try {
      const sal = (row.name || '').toString()
      const yearStr = new Date().getFullYear().toString()
      let out = html || ''

      // Grußformel / Anrede
      out = out.replace(/\{\s*salutation\s*\}/gi, sal)
      out = out.replace(/\{\s*saluation\s*\}/gi, sal) // bewusst die fehlerhafte Variante unterstützen

      // Jahr
      out = out.replace(/\{\s*year\s*\}/gi, yearStr)

      // Event-bezogene Platzhalter
      const ev = selectedEvent
      if (ev) {
        const evName = (ev.name || ev.title || ev.label || '').toString()
        const evLoc = (ev.location || ev.venue || ev.place || ev.city || '').toString()
        const fromStr = ev.startDate ? new Date(ev.startDate).toLocaleDateString() : ''
        const toStr = ev.endDate ? new Date(ev.endDate).toLocaleDateString() : ''

        out = out.replace(/\{\s*eventName\s*\}/gi, evName)
        out = out.replace(/\{\s*location\s*\}/gi, evLoc)
        out = out.replace(/\{\s*eventFrom\s*\}/gi, fromStr)
        out = out.replace(/\{\s*eventTo\s*\}/gi, toStr)
      }
      return out
    } catch {
      return html || ''
    }
  }, [selectedEvent])

  // Wählt Standardtext aus Templates, bevorzugt EVENT_INVOICE, sonst heuristisch
  const getDefaultEmailText = useCallback((row: ParticipantRow): string => {
    const pickBody = (t?: MailTemplate) => (t?.body || t?.text || t?.content || '').trim()
    let bodyFromTemplate = ''
    if (mailTemplates && mailTemplates.length > 0) {
      const byType = mailTemplates.find(t => (t as MailTemplate)?.type === 'PARTICIPATION_INVOICE') as MailTemplate | undefined
      const preferred = byType || (mailTemplates.find(t => {
        const key = (t?.key || t?.name || '').toLowerCase()
        return key.includes('event') || key.includes('veranstaltung') || key.includes('invoice') || key.includes('rechnung')
      }) as MailTemplate | undefined) || (mailTemplates[0] as MailTemplate)
      bodyFromTemplate = pickBody(preferred)
    }

    if (bodyFromTemplate) return applyMailPlaceholders(bodyFromTemplate, row)

    const name = row.name || 'Teilnehmer/in'
    const inv = row.invoiceNumber || ''
    const fallback = `Hallo ${name},\n\nbitte finden Sie im Anhang Ihre Veranstaltungsrechnung ${inv}.\n\nBei Rückfragen stehen wir Ihnen gerne zur Verfügung.\n\nMit freundlichen Grüßen\nIhr MarkenCamp Team`
    return applyMailPlaceholders(fallback, row)
  }, [mailTemplates, applyMailPlaceholders])

  const allChecked = useMemo(() => rows.length > 0 && rows.every(r => !!r.selected), [rows])
  const someChecked = useMemo(() => rows.some(r => !!r.selected) && !allChecked, [rows, allChecked])
  const selectedCount = useMemo(() => rows.filter(r => !!r.selected).length, [rows])

  const applySort = useCallback((arr: ParticipantRow[]): ParticipantRow[] => {
    const sign = sortDir === 'asc' ? 1 : -1
    const getVal = (r: ParticipantRow) => {
      const v: any = (r as any)[sortKey]
      if (v == null) return ''
      if (sortKey === 'sentAt') return v ? new Date(v).getTime() : 0
      return typeof v === 'string' ? v.toLowerCase() : v
    }
    const sorted = [...arr].sort((a, b) => {
      // Unsent first (sentAt null/empty)
      const aSent = !!a.sentAt
      const bSent = !!b.sentAt
      if (aSent !== bSent) return aSent ? 1 : -1
      const av = getVal(a)
      const bv = getVal(b)
      if (av < bv) return -1 * sign
      if (av > bv) return 1 * sign
      return 0
    })
    return sorted
  }, [sortDir, sortKey])

  const onRequestSort = (key: typeof sortKey) => {
    setSortKey(prevKey => {
      if (prevKey === key) {
        setSortDir(prevDir => (prevDir === 'asc' ? 'desc' : 'asc'))
        return prevKey
      }
      setSortDir('asc')
      return key
    })
    setRows(prev => applySort(prev))
  }

  const toggleAll = () => {
    const shouldCheckAll = !(rows.length > 0 && rows.every(r => !!r.selected))
    setRows(prev => prev.map(r => ({ ...r, selected: shouldCheckAll })))
  }

  const toggleRow = (id: string) => {
    setRows(prev => prev.map(r => (r.id === id ? { ...r, selected: !r.selected } : r)))
  }

  const requestHeaders = () => {
    const headers: HeadersInit = new Headers()
    headers.set('Accept', 'application/json')
    headers.set('Content-Type', 'application/json')
    addCsrfHeaders(headers)
    return headers
  }

  // Load all events for dropdown
  const loadEvents = useCallback(async () => {
    try {
      setLoadingEvents(true)
      const url = `${Env.basePath || ''}/api/admin/events`
      const res = await fetch(url, { method: 'GET', credentials: 'include', headers: requestHeaders() })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(`HTTP ${res.status} ${text}`)
      }
      const json = await res.json().catch(() => [])
      const arr: EventInfo[] = Array.isArray(json) ? json : (json?.items || [])
      setEvents(arr)
      // Preselect first event if none selected
      const firstId = arr?.[0]?.id || arr?.[0]?.uuid
      if (!eventId && firstId) setEventId(String(firstId))
    } catch (e) {
      console.error('Events laden fehlgeschlagen', e)
      setEvents([])
    } finally {
      setLoadingEvents(false)
    }
  }, [eventId])

  const loadParticipants = useCallback(async () => {
    if (!eventId) return
    try {
      setLoading('load')
      const url = `${Env.basePath || ''}/api/admin/events/${encodeURIComponent(eventId)}/participants`
      const res = await fetch(url, { method: 'GET', credentials: 'include', headers: requestHeaders() })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(`HTTP ${res.status} ${text}`)
      }
      const json = await res.json().catch(() => [])
      const mapped: ParticipantRow[] = (Array.isArray(json) ? json : (json?.items || [])).map((p: any) => ({
        id: p.id || p.participationId || p.uuid || p.invoiceId || Math.random().toString(36).slice(2),
        name: p.name || p.participantName || p.contactName || [p.firstName, p.lastName].filter(Boolean).join(' ') || undefined,
        participationOptionName: p.participationOptionName || p.optionName || undefined,
        email: p.email || p.contactEmail || undefined,
        invoiceNumber: p.invoiceNumber || undefined,
        invoiceId: p.invoiceId || undefined,
        sentAt: p.sent || p.sentAt || null,
        selected: !(p.sent || p.sentAt),
        amountOpen: p.amountOpen || 0,
        paid: p.paid || false,
        amountGross: p.amountGross || 0,
      }))
      setRows(applySort(mapped))
    } catch (e) {
      console.error('Teilnehmer laden fehlgeschlagen', e)
      setRows([])
    } finally {
      setLoading(null)
    }
  }, [eventId, applySort])

  const handleGenerateInvoices = useCallback(async () => {
    if (!eventId) return
    try {
      setLoading('generate')
      const url = `${Env.basePath || ''}/api/admin/events/${encodeURIComponent(eventId)}/invoices`
      const res = await fetch(url, { method: 'POST', credentials: 'include', headers: requestHeaders() })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(`HTTP ${res.status} ${text}`)
      }
      setSnackMsg('Rechnungen für die Veranstaltung wurden erzeugt.')
      setSnackOpen(true)
      // Nach dem Erzeugen neu laden
      await loadParticipants()
    } catch (e) {
      console.error('Erzeugen der Veranstaltungsrechnungen fehlgeschlagen', e)
      setSnackMsg('Erzeugen fehlgeschlagen. Siehe Konsole für Details.')
      setSnackOpen(true)
    } finally {
      setLoading(null)
    }
  }, [eventId, loadParticipants])

  const handleSendInvoices = useCallback(async () => {
    if (!eventId) return
    try {
      setLoading('send')
      const toSend = rows.filter(r => !!r.selected && !!r.invoiceId)
      if (toSend.length === 0) {
        setSnackMsg('Keine Auswahl zum Versenden vorhanden.')
        setSnackOpen(true)
        return
      }

      const headers: HeadersInit = new Headers()
      headers.set('Accept', 'application/json')
      headers.set('Content-Type', 'application/json')
      addCsrfHeaders(headers)

      const promises = toSend.map(async (r) => {
        const url = `${Env.basePath || ''}/api/admin/events/${encodeURIComponent(eventId)}/invoices/${r.invoiceId}/mail`
        const body = { invoiceId: r.invoiceId }
        const res = await fetch(url, { method: 'POST', credentials: 'include', headers, body: JSON.stringify(body) })
        if (!res.ok) {
          const text = await res.text().catch(() => '')
          throw new Error(`HTTP ${res.status} ${text}`)
        }
        return r.invoiceId as string
      })

      const results = await Promise.allSettled(promises)
      const succeededIds = results.filter((r): r is PromiseFulfilledResult<string> => r.status === 'fulfilled').map(r => r.value)
      const succeeded = succeededIds.length
      const failed = results.length - succeeded

      if (succeededIds.length > 0) {
        const nowIso = new Date().toISOString()
        setRows(prev => applySort(prev.map(r => (succeededIds.includes(r.invoiceId || '') ? { ...r, selected: false, sentAt: nowIso } : r))))
      }

      setSnackMsg(`E-Mails versendet: ${succeeded}. Fehlgeschlagen: ${failed}.`)
      setSnackOpen(true)
    } catch (e) {
      console.error('Versand der Veranstaltungsrechnungen fehlgeschlagen', e)
      setSnackMsg('Versand fehlgeschlagen. Siehe Konsole für Details.')
      setSnackOpen(true)
    } finally {
      setLoading(null)
    }
  }, [eventId, rows, applySort])

  // Helper to trigger a browser download for a Blob
  const createAndDownloadFileFromBlob = (data: Blob, filename: string): void => {
    const dataUrl = window.URL.createObjectURL(data)
    const tempLink = document.createElement('a')
    tempLink.href = dataUrl
    tempLink.setAttribute('download', filename)
    tempLink.click()
    setTimeout(() => URL.revokeObjectURL(dataUrl), 60_000)
  }

  const handleDownloadAll = useCallback(async () => {
    if (!eventId) return
    try {
      setLoading('download')
      const url = `${Env.basePath || ''}/api/admin/events/${encodeURIComponent(eventId)}/invoices/zip`
      const headers: HeadersInit = new Headers()
      headers.set('Accept', 'application/zip, application/octet-stream')
      addCsrfHeaders(headers)
      const res = await fetch(url, { method: 'GET', credentials: 'include', headers })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(`ZIP Download fehlgeschlagen: HTTP ${res.status} ${text}`)
      }
      const disposition = res.headers.get('Content-Disposition') || res.headers.get('content-disposition')
      let filename = 'Veranstaltungsrechnungen.zip'
      if (disposition) {
        const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(disposition)
        const raw = decodeURIComponent(match?.[1] || '') || match?.[2]
        if (raw) filename = raw
      }
      const blob = await res.blob()
      createAndDownloadFileFromBlob(blob, filename)
    } catch (e) {
      console.error(e)
      alert('ZIP konnte nicht heruntergeladen werden.')
    } finally {
      setLoading(null)
    }
  }, [eventId])

  useEffect(() => {
    // Events für Dropdown laden
    loadEvents()
  }, [loadEvents])

  // Mailtemplates laden (best-effort, blockiert nichts)
  useEffect(() => {
    let canceled = false
    const loadTemplates = async () => {
      try {
        const headers: HeadersInit = new Headers()
        headers.set('Accept', 'application/json')
        addCsrfHeaders(headers)
        const url = `${Env.basePath || ''}/api/admin/mailtemplates/`
        const res = await fetch(url, { method: 'GET', credentials: 'include', headers })
        if (!res.ok) return
        const json = await res.json().catch(() => [])
        const arr: MailTemplate[] = Array.isArray(json) ? json : (json?.items || [])
        if (!canceled) setMailTemplates(arr)
      } catch (e) {
        console.warn('Mailtemplates konnten nicht geladen werden', e)
      }
    }
    loadTemplates()
    return () => { canceled = true }
  }, [])

  // Auto-load participants whenever the selected event changes (incl. initial default selection)
  useEffect(() => {
    if (!eventId) return
    // load participants for the selected event
    loadParticipants()
  }, [eventId, loadParticipants])

  // Resort rows when sort state changes
  useEffect(() => {
    setRows(prev => applySort(prev))
  }, [sortKey, sortDir, applySort])

  const formatDate = (d?: string | Date | null) => {
    if (!d) return '–'
    const dt = new Date(d)
    return isNaN(dt.getTime()) ? '–' : dt.toLocaleDateString()
  }

  // Downloads the PDF for an event invoice by invoiceId
  const openPdfInNewTab = async (invoiceId: string) => {
    if (!eventId) return
    try {
      setLoading('pdf')
      const url = `${Env.basePath || ''}/api/admin/events/${encodeURIComponent(eventId)}/invoices/${encodeURIComponent(invoiceId)}/pdf`
      const headers: HeadersInit = new Headers()
      headers.set('Accept', 'application/pdf')
      addCsrfHeaders(headers)

      const res = await fetch(url, { method: 'GET', credentials: 'include', headers })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(`PDF Download fehlgeschlagen: HTTP ${res.status} ${text}`)
      }
      const disposition = res.headers.get('Content-Disposition') || res.headers.get('content-disposition')
      let filename = `Rechnung-${invoiceId}.pdf`
      if (disposition) {
        const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(disposition)
        const raw = decodeURIComponent(match?.[1] || '') || match?.[2]
        if (raw) filename = raw
      }
      const blob = await res.blob()
      createAndDownloadFileFromBlob(blob, filename)
    } catch (e) {
      console.error(e)
      alert('PDF konnte nicht geladen werden.')
    } finally {
      setLoading(null)
    }
  }

  const renderInvoiceLink = (row: ParticipantRow) => {
    if (!row.invoiceNumber) return '–'
    if (!row.invoiceId) return row.invoiceNumber
    return (
      <Link
        href='#'
        underline='hover'
        onClick={(e: any) => {
          e.preventDefault()
          openPdfInNewTab(row.invoiceId as string)
        }}
      >
        {row.invoiceNumber}
      </Link>
    )
  }

  // Per-row email dialog handlers
  const openSendDialog = (row: ParticipantRow) => {
    setActiveRow(row)
    // Standardtext aus Mailtemplates bestimmen (analog Invoice.tsx), Fallback integriert
    const text = getDefaultEmailText(row)
    setEmailText(text)
    setSendDialogOpen(true)
  }

  const closeSendDialog = () => {
    setSendDialogOpen(false)
    setActiveRow(null)
    setEmailText('')
    setSendingSingle(false)
  }

  const handleConfirmSendSingle = async () => {
    if (!activeRow || !activeRow.invoiceId || !eventId) return
    try {
      setSendingSingle(true)
      const headers: HeadersInit = new Headers()
      headers.set('Accept', 'application/json')
      headers.set('Content-Type', 'application/json')
      addCsrfHeaders(headers)
      const body = {
        invoiceId: activeRow.invoiceId,
        alternativeBody: emailText,
        alternativeSubject: null,
      }
      const url = `${Env.basePath || ''}/api/admin/events/${encodeURIComponent(eventId)}/invoices/${activeRow.invoiceId}/mail`
      const res = await fetch(url, { method: 'POST', credentials: 'include', headers, body: JSON.stringify(body) })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(`HTTP ${res.status} ${text}`)
      }
      const nowIso = new Date().toISOString()
      setRows(prev => applySort(prev.map(r => (r.id === activeRow.id ? { ...r, selected: false, sentAt: nowIso } : r))))
      setSnackMsg(`E-Mail versendet: ${activeRow.invoiceNumber || activeRow.id}`)
      setSnackOpen(true)
      setSendDialogOpen(false)
    } catch (e) {
      console.error('Fehler beim Einzelversand', e)
      setSnackMsg('Einzelversand fehlgeschlagen. Bitte prüfen Sie die Konsole für Details.')
      setSnackOpen(true)
    } finally {
      setSendingSingle(false)
    }
  }

  const openPaymentDialog = (row: ParticipantRow) => {
    setActiveRow(row)
    setPaymentAmount('')
    setPaymentDate(new Date().toISOString().split('T')[0])
    setPaymentDialogOpen(true)
  }

  const closePaymentDialog = () => {
    setPaymentDialogOpen(false)
    if (!sendDialogOpen) setActiveRow(null)
  }

  const handleCreatePayment = async () => {
    if (!activeRow?.invoiceId || !paymentAmount) return
    setIsSubmittingPayment(true)
    try {
      const headers: HeadersInit = new Headers()
      headers.set('Accept', 'application/json')
      addCsrfHeaders(headers)
      const url = `${Env.basePath || ''}/api/payments/create?invoiceId=${encodeURIComponent(activeRow.invoiceId)}&amount=${encodeURIComponent(paymentAmount)}&date=${encodeURIComponent(paymentDate)}`
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers,
      })
      if (res.ok) {
        setSnackMsg('Zahlung wurde erfolgreich erfasst.')
        setSnackOpen(true)
        closePaymentDialog()
        // Daten neu laden
        loadParticipants()
      } else {
        const text = await res.text().catch(() => '')
        alert(`Fehler beim Erfassen der Zahlung: ${res.status} ${text}`)
      }
    } catch (e) {
      console.error(e)
      alert('Fehler beim Erfassen der Zahlung.')
    } finally {
      setIsSubmittingPayment(false)
    }
  }

  useEffect(() => {
    if (targetInvoiceId && rows.length > 0) {
      const targetRow = rows.find(r => r.invoiceId === targetInvoiceId)
      if (targetRow) {
        openPaymentDialog(targetRow)
      }
    }
  }, [targetInvoiceId, rows])

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <h2>Veranstaltungsrechnungen</h2>
      </Grid>
      <Grid item xs={12}>
        <div className={classes.topBar}>
          <FormControl size='small' style={{ minWidth: 320 }}>
            <InputLabel id='event-select-label'>Veranstaltung</InputLabel>
            <Select
              labelId='event-select-label'
              id='event-select'
              value={eventId}
              label='Veranstaltung'
              onChange={(e: any) => setEventId(e.target.value as string)}
              disabled={loadingEvents}
            >
              {events.length === 0 && (
                <MenuItem value=''>Keine Veranstaltungen</MenuItem>
              )}
              {events.map((ev, idx) => {
                const id = String(ev.id || ev.uuid || idx)
                const label = ev.name || ev.title || ev.label || id
                return (
                  <MenuItem key={id} value={id}>{label}</MenuItem>
                )
              })}
            </Select>
          </FormControl>
          <Button variant='contained' color='primary' startIcon={<AutoAwesomeIcon />} onClick={handleGenerateInvoices} disabled={disableActions}>
            Alle Rechnungen neu generieren
          </Button>
          <Button
            variant='outlined'
            color='primary'
            startIcon={<DownloadIcon />} onClick={handleDownloadAll} disabled={disableActions}
          >
            Alle Rechnungen herunterladen
          </Button>
          <Button variant='contained' color='secondary' startIcon={<SendIcon />} onClick={handleSendInvoices} disabled={disableActions || selectedCount === 0}>
            E-Mails versenden ({selectedCount})
          </Button>
        </div>
      </Grid>
      <Grid item xs={12}>
        <TableContainer component={Paper}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell padding='checkbox'>
                  <Checkbox
                    indeterminate={someChecked}
                    checked={allChecked}
                    onChange={toggleAll}
                    inputProps={{ 'aria-label': 'Alle auswählen' }}
                  />
                </TableCell>
                <TableCell className={classes.tableHeadCell} sortDirection={sortKey === 'name' ? sortDir : false}>
                  <TableSortLabel
                    active={sortKey === 'name'}
                    direction={sortKey === 'name' ? sortDir : 'asc'}
                    onClick={() => onRequestSort('name')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.tableHeadCell} sortDirection={sortKey === 'participationOptionName' ? sortDir : false}>
                  <TableSortLabel
                    active={sortKey === 'participationOptionName'}
                    direction={sortKey === 'participationOptionName' ? sortDir : 'asc'}
                    onClick={() => onRequestSort('participationOptionName')}
                  >
                    Teilnahmeoption
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.tableHeadCell} sortDirection={sortKey === 'email' ? sortDir : false}>
                  <TableSortLabel
                    active={sortKey === 'email'}
                    direction={sortKey === 'email' ? sortDir : 'asc'}
                    onClick={() => onRequestSort('email')}
                  >
                    E-Mail
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.tableHeadCell} sortDirection={sortKey === 'invoiceNumber' ? sortDir : false}>
                  <TableSortLabel
                    active={sortKey === 'invoiceNumber'}
                    direction={sortKey === 'invoiceNumber' ? sortDir : 'asc'}
                    onClick={() => onRequestSort('invoiceNumber')}
                  >
                    Rechnungsnummer
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.tableHeadCell} sortDirection={sortKey === 'amountOpen' ? sortDir : false}>
                  <TableSortLabel
                    active={sortKey === 'amountOpen'}
                    direction={sortKey === 'amountOpen' ? sortDir : 'asc'}
                    onClick={() => onRequestSort('amountOpen')}
                  >
                    offener Betrag (brutto)
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.tableHeadCell} sortDirection={sortKey === 'sentAt' ? sortDir : false}>
                  <TableSortLabel
                    active={sortKey === 'sentAt'}
                    direction={sortKey === 'sentAt' ? sortDir : 'asc'}
                    onClick={() => onRequestSort('sentAt')}
                  >
                    Versendet am
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.tableHeadCell}>Status</TableCell>
                <TableCell className={classes.tableHeadCell} align='right'>Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell padding='checkbox'>
                    <Checkbox checked={!!r.selected} onChange={() => toggleRow(r.id)} />
                  </TableCell>
                  <TableCell>{r.name || '–'}</TableCell>
                  <TableCell>{r.participationOptionName || '–'}</TableCell>
                  <TableCell>{r.email || '–'}</TableCell>
                  <TableCell>{renderInvoiceLink(r)}</TableCell>
                  <TableCell>{(r.amountOpen || 0).toFixed(2)} €</TableCell>
                  <TableCell>{formatDate(r.sentAt)}</TableCell>
                  <TableCell>
                    {r.paid ? (
                      <Chip label='Bezahlt' color='success' size='small' />
                    ) : r.amountOpen !== undefined && r.amountGross !== undefined && r.amountOpen < r.amountGross && r.amountOpen > 0 ? (
                      <Chip label='Teilweise offen' color='warning' size='small' />
                    ) : (
                      <Chip label='Offen' color='primary' size='small' />
                    )}
                  </TableCell>
                  <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
                    <Button
                      size='small' variant='text'
                      color='secondary' startIcon={<SendIcon />} onClick={() => openSendDialog(r)} disabled={!r.invoiceId || loading !== null}
                    >
                      Rechnung absenden
                    </Button>
                    <Button
                      size='small' variant='text'
                      color='primary' startIcon={<PaymentsIcon />} onClick={() => openPaymentDialog(r)} disabled={!r.invoiceId || loading !== null}
                      sx={{ ml: 1 }}
                    >
                      Zahlung
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align='center'>Keine Daten</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      {/* Dialog: Einzelne E-Mail versenden */}
      <Dialog open={sendDialogOpen} onClose={closeSendDialog} fullWidth maxWidth='md'>
        <DialogTitle>
          Rechnung per E-Mail senden
          {activeRow && (
            <div style={{ fontSize: 12, fontWeight: 400, color: '#666', marginTop: 4 }}>
              Empfänger: {activeRow.email || '–'} • Rechnungsnummer: {activeRow.invoiceNumber || '–'}
            </div>
          )}
        </DialogTitle>
        <DialogContent>
          <div style={{ margin: '8px 0' }}>
            <ReactQuill theme='snow' value={emailText} onChange={setEmailText} modules={quillModules} formats={quillFormats} />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSendDialog} disabled={sendingSingle}>Abbrechen</Button>
          <Button variant='contained' color='primary' onClick={handleConfirmSendSingle} disabled={sendingSingle || !activeRow?.invoiceId}>Senden</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={paymentDialogOpen} onClose={closePaymentDialog} fullWidth maxWidth='xs'>
        <DialogTitle>Zahlungseingang erfassen
          {activeRow && (
            <div style={{ fontSize: 12, fontWeight: 400, color: '#666', marginTop: 4 }}>
              Rechnungsnummer: {activeRow.invoiceNumber || '–'} • Offener Betrag: {(activeRow.amountOpen || 0).toFixed(2)} €
            </div>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label='Betrag'
              type='number'
              fullWidth
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              InputProps={{ inputProps: { step: '0.01' } }}
              required
            />
            <TextField
              label='Datum'
              type='date'
              fullWidth
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePaymentDialog} disabled={isSubmittingPayment}>Abbrechen</Button>
          <Button
            variant='contained'
            color='primary'
            onClick={handleCreatePayment}
            disabled={isSubmittingPayment || !paymentAmount || !paymentDate}
          >
            Zahlung speichern
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackOpen} onClose={() => setSnackOpen(false)} message={snackMsg} autoHideDuration={4000} />
    </Grid>
  )
}

export default EventInvoice
